import * as React from "react";
import { PropsWithChildren } from "react";

import { DefaultErrorFallback } from "lib/error/DefaultErrorFallback";
import { NotFoundError } from "lib/error/NotFoundError";
import { NotFoundPage } from "lib/error/NotFoundPage";

interface ErrorBoundaryProps {
  fallback?: (error: Error) => React.ReactNode;
  useDefaultFallback?: boolean;
}

export function ErrorBoundary(props: PropsWithChildren<ErrorBoundaryProps>) {
  const { fallback, useDefaultFallback, children } = props;

  const fallBackToUse = (error: Error) => {
    if (error.name === "ChunkLoadError") {
      const reloadCount = Number.parseInt(
        sessionStorage.getItem("reloadCount") ?? "0",
        10,
      );
      if (reloadCount < 3) {
        sessionStorage.setItem("reloadCount", String(reloadCount + 1));

        if ("request" in error) {
          // Not certain if this will work, but if an asset fails to load because it is corrupted
          // in the cache, this *should* force the browser to refetch it and store it in the
          // cache, hopefully resolving any cache corruption issues.
          const { request } = error;
          void fetch(String(request), { method: "GET", cache: "reload" })
            .catch()
            .finally(() => window.location.reload());
        } else {
          window.location.reload();
        }
      }
    }
    if (error instanceof NotFoundError) {
      return <NotFoundPage />;
    }

    if (!useDefaultFallback && fallback) {
      return fallback(error);
    }

    return <DefaultErrorFallback error={error} />;
  };

  return (
    <InternalErrorBoundary onError={fallBackToUse}>
      {children}
    </InternalErrorBoundary>
  );
}

interface InternalErrorBoundaryProps {
  onError: (error: Error) => React.ReactNode;
  children: React.ReactNode;
}

class InternalErrorBoundary extends React.Component<
  InternalErrorBoundaryProps,
  { hasError: false } | { hasError: true; error: Error }
> {
  constructor(props: InternalErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  override componentDidCatch(error: Error) {
    this.setState({ hasError: true, error });
  }

  override render() {
    if (this.state.hasError) {
      const { onError } = this.props;
      return onError(this.state.error);
    }

    return this.props.children;
  }
}
